import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, DollarSign, Copy, Check, RefreshCw, Gift, Zap,
  Twitter, Send, Link2, Trophy, ChevronRight, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatUSD = (value) => {
  if (typeof value === 'string') return value;
  return `$${value?.toFixed(2) || '0.00'}`;
};

export const Referrals = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [copied, setCopied] = useState(false);

  // Generate referral code from user or API stats
  const referralCode = stats?.referral_code || user?.referral_code || `PS${user?.id?.slice(-6).toUpperCase() || 'TEST0000'}`;
  const referralLink = stats?.referral_link || `https://profitswarm.com/register?ref=${referralCode}`;

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json' 
      };
      
      // Get user_id from auth context
      const userId = user?.id || '';

      const [statsRes, referralsRes] = await Promise.all([
        fetch(`${API_URL}/api/referrals/stats?user_id=${userId}`, { headers }).catch(() => null),
        fetch(`${API_URL}/api/referrals/list?user_id=${userId}`, { headers }).catch(() => null),
      ]);

      if (statsRes?.ok) setStats(await statsRes.json());
      if (referralsRes?.ok) {
        const data = await referralsRes.json();
        setReferrals(data.referrals || []);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Link copiado al portapapeles');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(referralCode);
    toast.success('Código copiado');
  };

  const shareTwitter = () => {
    const text = `Únete a Invictus - Inversión Web3 con hasta 9% mensual en stablecoins. Usa mi código: ${referralCode}`;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareTelegram = () => {
    const text = `Únete a Invictus - Inversión Web3 con hasta 9% mensual en stablecoins. Usa mi código: ${referralCode}`;
    window.open(`https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(text)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Mock data if API not connected
  const displayStats = stats || {
    total_referrals: 0,
    level1_count: 0,
    level2_count: 0,
    total_earned: 0,
    pending: 0,
    withdrawn: 0,
  };

  return (
    <div className="space-y-8 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Gift className="w-8 h-8 text-amber-400" />
            Programa de Referidos
          </h1>
          <p className="text-muted-foreground mt-1">
            Gana el <strong className="text-amber-400">2% mensual</strong> del capital invertido por cada referido directo
          </p>
        </div>
        <Button variant="outline" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
          Actualizar
        </Button>
      </div>

      {/* Referral Link Card */}
      <Card className="bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent border-amber-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-amber-400" />
            Tu Link de Referido
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-card/50 border border-border rounded-lg px-4 py-3 font-mono text-sm truncate">
              {referralLink}
            </div>
            <Button onClick={copyLink} variant="outline" className="shrink-0">
              {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">Código:</span>
            <Badge 
              className="bg-amber-500/20 text-amber-400 px-3 py-1 cursor-pointer hover:bg-amber-500/30"
              onClick={copyCode}
            >
              {referralCode}
            </Badge>
            <span className="text-sm text-muted-foreground ml-4">Compartir:</span>
            <Button variant="ghost" size="sm" onClick={shareTwitter}>
              <Twitter className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={shareTelegram}>
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Users className="w-8 h-8 text-amber-400" />
            </div>
            <p className="text-3xl font-bold">{displayStats.total_referrals}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Referidos</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <DollarSign className="w-8 h-8 text-green-400" />
            </div>
            <p className="text-3xl font-bold text-green-400">{formatUSD(displayStats.total_earned)}</p>
            <p className="text-xs text-muted-foreground mt-1">Ganancias Totales</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Zap className="w-8 h-8 text-yellow-400" />
            </div>
            <p className="text-3xl font-bold text-yellow-400">{formatUSD(displayStats.pending)}</p>
            <p className="text-xs text-muted-foreground mt-1">Pendiente</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-3">
              <Trophy className="w-8 h-8 text-blue-400" />
            </div>
            <p className="text-3xl font-bold">{formatUSD(displayStats.withdrawn)}</p>
            <p className="text-xs text-muted-foreground mt-1">Retirado</p>
          </CardContent>
        </Card>
      </div>

      {/* Commission Model */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-400" />
            Cómo funciona
          </CardTitle>
          <CardDescription>
            2% mensual del capital invertido por tus referidos directos — recurrente cada mes
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-6 rounded-xl bg-gradient-to-br from-amber-500/10 to-transparent border border-amber-500/30">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold">Comisión recurrente</h3>
                <p className="text-sm text-muted-foreground">Por cada referido directo (Nivel 1)</p>
              </div>
              <Badge className="bg-amber-500/20 text-amber-400 text-lg px-4 py-2" data-testid="referral-rate-badge">
                2% / mes
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Ganas <strong className="text-amber-400">el 2% mensual del capital invertido</strong> por cada usuario que invites,
              durante <strong>toda la vida</strong> de su inversión. El bonus se acredita cada mes junto con los payouts.
            </p>

            <div className="bg-black/30 rounded-lg p-4 border border-amber-500/20">
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Ejemplo</p>
              <p className="text-sm">
                <span className="font-semibold text-amber-300">Manuel</span> invita a su primo{' '}
                <span className="font-semibold text-amber-300">David</span>. David invierte{' '}
                <strong className="text-emerald-400">$50,000</strong> en cualquier paquete.
              </p>
              <p className="text-sm mt-2">
                → Manuel recibe <strong className="text-emerald-400">$1,000 cada mes</strong> mientras
                David mantenga su capital invertido.
              </p>
            </div>

            <div className="mt-4 pt-4 border-t border-amber-500/20">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Mis referidos directos:</span>
                <span className="font-bold text-amber-400" data-testid="referral-count">
                  {displayStats.level1_count || displayStats.total_referrals || 0} usuarios
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referrals Table */}
      <Card>
        <CardHeader>
          <CardTitle>Mis Referidos</CardTitle>
          <CardDescription>
            Usuarios que se registraron con tu código
          </CardDescription>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold mb-2">Sin referidos aún</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Comparte tu link para empezar a ganar comisiones
              </p>
              <Button onClick={copyLink}>
                <Copy className="w-4 h-4 mr-2" />
                Copiar Link
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Usuario</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead className="text-right">Ganancia Generada</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((ref, i) => (
                  <TableRow key={i}>
                    <TableCell className="font-medium">{ref.name || ref.email}</TableCell>
                    <TableCell>
                      <Badge variant={ref.level === 1 ? "default" : "secondary"}>
                        Nivel {ref.level}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(ref.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right text-green-400">
                      {formatUSD(ref.earnings_generated || 0)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Referrals;
